// Stream consumer transport: connects to WS /streams/{id}?token=... using the
// one-time consumer token from a tools/call stream descriptor. Frames are
// Zod-validated (StreamWsFrame); the caller receives typed chunk/status events.
// A stream token is audience-bound + single-stream-scoped (SPEC §5A).

import {
  parseStreamFrame,
  isStreamStatus,
  type StreamChunkFrame,
  type StreamStatusFrame,
} from '../api/events';
import { createWsClient, type WsClient } from './ws';
import { getWebSocketImpl } from './wsImpl';

export interface StreamConsumerOptions {
  /** Full ws(s):// URL from the descriptor (already includes ?token=...). */
  wsUrl: string;
  onChunk?: (chunk: StreamChunkFrame) => void;
  onStatus?: (status: StreamStatusFrame) => void;
  onInvalid?: (issues: unknown) => void;
  /** Override the WebSocket implementation (defaults to the platform/mock impl). Used in tests. */
  WebSocketImpl?: typeof WebSocket;
}

export function connectStream(opts: StreamConsumerOptions): WsClient {
  const client = createWsClient({
    url: opts.wsUrl,
    parse: 'json',
    WebSocketImpl: opts.WebSocketImpl ?? getWebSocketImpl(),
    reconnectMs: 1000,
    onMessage: (data) => {
      const r = parseStreamFrame(data);
      if (!r.ok) {
        opts.onInvalid?.(r.error.issues);
        return;
      }
      if (isStreamStatus(r.value)) opts.onStatus?.(r.value);
      else opts.onChunk?.(r.value);
    },
  });
  client.connect();
  return client;
}
